import '../styles/IntegrationsConfigPanel.css';
import React, { useEffect, useRef, useState } from 'react';
import { useProjectCollection, projectData } from '../lib/projectData';

const COLLECTION_KEY = 'integration_config';

const STATUS_META = {
  authorized: { label: 'Authorized', cls: 'authorized' },
  failing: { label: 'Failing', cls: 'failing' },
  untested: { label: 'Untested', cls: 'untested' },
};

const CREDENTIAL_MODES = [
  { value: 'stored locally', label: 'Stored locally' },
  { value: 'prompt at test', label: 'Prompt at test' },
  { value: 'not set', label: 'Not set' },
  { value: 'bound secret', label: 'Bind a secret reference' },
];

const BLANK_DRAFT = {
  serviceName: '',
  endpoint: '',
  credentialMode: 'stored locally',
  secret: '',
  authorizedOnly: false,
};

const errMsg = (err, fallback) => {
  if (!err) return fallback;
  if (typeof err === 'string') return err;
  return err.message || fallback;
};

const fmtStamp = (iso) => {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())} ${pad(d.getUTCHours())}:${pad(d.getUTCMinutes())} UTC`;
};

const draftFrom = (record) => ({
  serviceName: record.serviceName || '',
  endpoint: record.endpoint || '',
  credentialMode: record.credentialRef || 'not set',
  secret: '',
  authorizedOnly: false,
});

function StatusBadge({ state }) {
  const meta = STATUS_META[state] || STATUS_META.untested;
  return <span className={`icp-badge icp-badge--${meta.cls}`}>{meta.label}</span>;
}

export default function IntegrationsConfigPanel({ onTestConnection } = {}) {
  const { data, loading, error, refresh } = useProjectCollection(COLLECTION_KEY);
  const records = Array.isArray(data) ? data : [];

  const [selectedConfigId, setSelectedConfigId] = useState(null);
  const [draft, setDraft] = useState(BLANK_DRAFT);
  const [formOpen, setFormOpen] = useState(false);
  const [validationErrors, setValidationErrors] = useState([]);
  const [saveStatus, setSaveStatus] = useState('idle'); // idle | saving | saved | error
  const [saveError, setSaveError] = useState('');
  const [savedNotice, setSavedNotice] = useState(null);
  const [testStatus, setTestStatus] = useState('idle'); // idle | testing | ok | fail
  const [testFailureReason, setTestFailureReason] = useState('');
  const [testedId, setTestedId] = useState(null);
  const [removeConfirmId, setRemoveConfirmId] = useState(null);
  const [readRetrying, setReadRetrying] = useState(false);
  const [notice, setNotice] = useState(null);

  const summaryRef = useRef(null);
  const flashRef = useRef(null);
  const nameRef = useRef(null);
  const endpointRef = useRef(null);

  const fieldError = (name) => validationErrors.find((e) => e.field === name);

  useEffect(() => {
    if (validationErrors.length && summaryRef.current) summaryRef.current.focus();
  }, [validationErrors]);

  useEffect(() => {
    if (savedNotice && flashRef.current) flashRef.current.focus();
  }, [savedNotice]);

  const setField = (name, value) => {
    setDraft((d) => ({ ...d, [name]: value }));
    setValidationErrors((errs) => errs.filter((e) => e.field !== name));
    if (testStatus !== 'idle') {
      setTestStatus('idle');
      setTestFailureReason('');
    }
  };

  const openAdd = () => {
    setSelectedConfigId(null);
    setDraft(BLANK_DRAFT);
    setValidationErrors([]);
    setSaveError('');
    setSaveStatus('idle');
    setTestStatus('idle');
    setTestFailureReason('');
    setFormOpen(true);
  };

  const openEdit = (record) => {
    setSelectedConfigId(record.id);
    setDraft(draftFrom(record));
    setValidationErrors([]);
    setSaveError('');
    setSaveStatus('idle');
    setTestStatus('idle');
    setTestFailureReason('');
    setFormOpen(true);
  };

  const cancelEdit = () => {
    setSelectedConfigId(null);
    setDraft(BLANK_DRAFT);
    setValidationErrors([]);
    setSaveError('');
    setSaveStatus('idle');
    setTestStatus('idle');
    setTestFailureReason('');
    setFormOpen(false);
  };

  const validate = () => {
    const errs = [];
    const name = draft.serviceName.trim();
    const endpoint = draft.endpoint.trim();
    if (!name) errs.push({ field: 'serviceName', message: 'Service name is required.' });
    else if (name.length > 80) errs.push({ field: 'serviceName', message: 'Service name must be 80 characters or fewer.' });
    if (!endpoint) errs.push({ field: 'endpoint', message: 'Endpoint URL is required.' });
    else if (!/^https:\/\/[^\s/]+\.[^\s]+/i.test(endpoint)) {
      errs.push({ field: 'endpoint', message: 'Endpoint must be a full HTTPS URL, for example https://host.internal/api.' });
    }
    if (draft.credentialMode === 'bound secret' && !draft.secret) {
      errs.push({ field: 'secret', message: 'Enter the secret reference to bind to this endpoint.' });
    }
    if (!draft.authorizedOnly) {
      errs.push({ field: 'authorizedOnly', message: 'Confirm this endpoint is an authorized target before saving.' });
    }
    return errs;
  };

  const handleSave = async (event) => {
    event.preventDefault();
    const errs = validate();
    setValidationErrors(errs);
    if (errs.length) {
      setSaveStatus('idle');
      setSaveError('');
      return;
    }
    const now = new Date().toISOString();
    const previous = records.find((r) => r.id === selectedConfigId);
    const isRetestSame = (rec) => rec.endpoint.trim() === draft.endpoint.trim();
    const credentialRef = draft.credentialMode === 'bound secret' ? draft.secret : draft.credentialMode;
    const payload = {
      serviceName: draft.serviceName.trim(),
      endpoint: draft.endpoint.trim(),
      credentialRef,
      updatedAt: now,
    };
    if (!previous || !isRetestSame(previous)) payload.authorizationState = 'untested';

    setSaveStatus('saving');
    setSaveError('');
    try {
      if (selectedConfigId) {
        await projectData.update(COLLECTION_KEY, selectedConfigId, payload);
      } else {
        await projectData.create(COLLECTION_KEY, { ...payload, lastTestedAt: '', authorizationState: 'untested' });
      }
      const savedLabel = draft.serviceName.trim();
      setSaveStatus('saved');
      setSavedNotice({ serviceName: savedLabel, endpoint: payload.endpoint, timestamp: now });
      setSelectedConfigId(null);
      setDraft(BLANK_DRAFT);
      setFormOpen(false);
      setTestStatus('idle');
      setTestFailureReason('');
    } catch (err) {
      setSaveStatus('error');
      setSaveError(errMsg(err, 'The configuration could not be saved.'));
    }
  };

  const runTest = async ({ record, id }) => {
    setTestStatus('testing');
    setTestFailureReason('');
    setTestedId(null);
    setSaveError('');
    if (typeof onTestConnection !== 'function') {
      setTestStatus('fail');
      setTestFailureReason('Connectivity testing is not available in this preview.');
      return;
    }
    try {
      const result = await onTestConnection({ serviceName: record.serviceName, endpoint: record.endpoint });
      if (result && result.ok === true) {
        setTestStatus('ok');
        setTestedId(id || null);
        setValidationErrors([]);
        setSaveError('');
        if (id) {
          await projectData.update(COLLECTION_KEY, id, {
            authorizationState: 'authorized',
            lastTestedAt: new Date().toISOString(),
          });
        }
      } else {
        const reason = result && result.reason ? result.reason : 'The connectivity test did not succeed.';
        setTestStatus('fail');
        setTestFailureReason(reason);
        setValidationErrors([]);
        setSaveError('');
        if (id) {
          await projectData.update(COLLECTION_KEY, id, {
            authorizationState: 'failing',
            lastTestedAt: new Date().toISOString(),
          });
        }
      }
    } catch (err) {
      setTestStatus('fail');
      setTestFailureReason(errMsg(err, 'The connectivity test could not complete.'));
      setSaveError('');
    }
  };

  const testDraft = () => {
    const errs = validate().filter((e) => e.field !== 'authorizedOnly');
    setValidationErrors(errs);
    if (errs.length) {
      setTestStatus('idle');
      return;
    }
    runTest({
      record: { serviceName: draft.serviceName.trim(), endpoint: draft.endpoint.trim() },
      id: selectedConfigId,
    });
  };

  const testRow = (record) => {
    setRemoveConfirmId(null);
    runTest({ record, id: record.id });
  };

  const removeRecord = async (id) => {
    setNotice(null);
    try {
      await projectData.remove(COLLECTION_KEY, id);
      setRemoveConfirmId(null);
      if (selectedConfigId === id) cancelEdit();
    } catch (err) {
      setRemoveConfirmId(null);
      setNotice({ kind: 'error', text: errMsg(err, 'The configuration could not be removed.') });
    }
  };

  const retryRead = async () => {
    setReadRetrying(true);
    try {
      await refresh();
    } catch (err) {
      setNotice({ kind: 'error', text: errMsg(err, 'Configurations could not be read.') });
    } finally {
      setReadRetrying(false);
    }
  };

  const heading = selectedConfigId ? 'Edit authorized service' : records.length ? 'Add authorized service' : 'Configure your first service';
  const ariaBase = 'icp';

  const renderReadError = () => (
    <div className="icp-state" role="alert">
      <h3>Could not read configurations</h3>
      <p>{errMsg(error, 'The local configuration store did not respond.')}</p>
      <button type="button" className="icp-btn" onClick={retryRead} disabled={readRetrying}>
        {readRetrying ? 'Retrying…' : 'Retry read'}
      </button>
    </div>
  );

  const renderList = () => (
    <section aria-labelledby={`${ariaBase}-list-title`}>
      <div className="icp-section-label" id={`${ariaBase}-list-title`}>
        Configured services
        <span className="icp-section-label__count">{records.length}</span>
      </div>
      <ul className="icp-list">
        {records.map((record) => {
          const stamp = fmtStamp(record.lastTestedAt);
          const selected = record.id === selectedConfigId;
          const confirming = record.id === removeConfirmId;
          const tested = record.id === testedId && testStatus === 'ok';
          return (
            <li
              key={record.id}
              className={`icp-row${selected ? ' icp-row--selected' : ''}${tested ? ' icp-row--tested' : ''}`}
              aria-current={selected ? 'true' : undefined}
            >
              <div className="icp-row__main">
                <div className="icp-row__topline">
                  <h3 className="icp-row__name">{record.serviceName}</h3>
                  <StatusBadge state={record.authorizationState} />
                </div>
                <code className="icp-row__endpoint">{record.endpoint}</code>
                <div className="icp-row__meta">
                  <span>credential: <strong>{record.credentialRef || 'not set'}</strong></span>
                  <span>updated <strong>{fmtStamp(record.updatedAt) || '—'}</strong></span>
                </div>
              </div>

              <div className="icp-row__meta icp-row__timestamp">
                <span>{stamp ? <>last tested <strong>{stamp}</strong></> : 'not yet tested'}</span>
              </div>

              <div className="icp-row__actions" aria-label={`Actions for ${record.serviceName}`}>
                <button
                  type="button"
                  className="icp-btn icp-btn--small"
                  aria-expanded={selected && formOpen}
                  aria-controls={`${ariaBase}-form`}
                  onClick={() => openEdit(record)}
                >
                  Edit
                </button>
                <button
                  type="button"
                  className="icp-btn icp-btn--small"
                  onClick={() => testRow(record)}
                  disabled={testStatus === 'testing'}
                >
                  Test connectivity
                </button>
                <button
                  type="button"
                  className="icp-btn icp-btn--small icp-btn--danger"
                  aria-expanded={confirming}
                  aria-controls={`${ariaBase}-confirm-${record.id}`}
                  onClick={() => setRemoveConfirmId(confirming ? null : record.id)}
                >
                  Remove
                </button>
              </div>

              <div
                id={`${ariaBase}-confirm-${record.id}`}
                className="icp-row__confirm"
                hidden={!confirming}
              >
                <span>Remove {record.serviceName}?</span>
                <button type="button" className="icp-btn icp-btn--small icp-btn--danger" onClick={() => removeRecord(record.id)}>
                  Yes, remove
                </button>
                <button type="button" className="icp-btn icp-btn--small" onClick={() => setRemoveConfirmId(null)}>
                  Keep it
                </button>
              </div>

              {record.id === testedId && (testStatus === 'ok' || testStatus === 'fail') && (
                <p className={`icp-row__notice${testStatus === 'ok' ? ' icp-row__notice--ok' : ''}`} role="status">
                  {testStatus === 'ok' ? (
                    <><strong>Connection succeeded.</strong> Marked authorized.</>
                  ) : (
                    <><strong>Connection failed.</strong> {testFailureReason} Open Edit to correct the endpoint and retest.</>
                  )}
                </p>
              )}
            </li>
          );
        })}
      </ul>
    </section>
  );

  const renderForm = () => {
    if (!formOpen && !records.length) {
      return (
        <div className="icp-state">
          <h3>No integrations configured</h3>
          <p>Add an authorized service endpoint to store it locally and test its connectivity.</p>
          <button type="button" className="icp-btn icp-btn--primary" onClick={openAdd}>Add authorized service</button>
        </div>
      );
    }
    if (!formOpen) {
      return (
        <div>
          <button type="button" className="icp-btn" aria-expanded="false" aria-controls={`${ariaBase}-form`} onClick={openAdd}>
            + Add authorized service
          </button>
        </div>
      );
    }
    const saving = saveStatus === 'saving';
    const testing = testStatus === 'testing';
    return (
      <form
        id={`${ariaBase}-form`}
        className="icp-form"
        noValidate
        onSubmit={handleSave}
        aria-labelledby={`${ariaBase}-form-title`}
      >
        <div className="icp-form__head">
          <h3 className="icp-form__title" id={`${ariaBase}-form-title`}>{heading}</h3>
          <span className="icp-form__scope">{selectedConfigId ? `editing ${selectedConfigId}` : 'new configuration'}</span>
        </div>

        {validationErrors.length > 0 && (
          <div className="icp-summary" ref={summaryRef} tabIndex={-1} role="alert">
            <p className="icp-summary__title">Fix {validationErrors.length} {validationErrors.length === 1 ? 'issue' : 'issues'} before saving</p>
            <ul>
              {validationErrors.map((e) => (
                <li key={e.field}>{e.message}</li>
              ))}
            </ul>
          </div>
        )}

        {saveStatus === 'error' && (
          <div className="icp-error" role="alert">
            <span className="icp-error__kicker">Save failed</span>
            <p className="icp-error__reason">{saveError}</p>
            <span className="icp-result">Your entered values are kept. Correct anything above and save again.</span>
          </div>
        )}

        <div className="icp-grid">
          <div className="icp-field">
            <label className="icp-label" htmlFor={`${ariaBase}-name`}>Service name</label>
            <input
              id={`${ariaBase}-name`}
              ref={nameRef}
              className="icp-input"
              type="text"
              value={draft.serviceName}
              maxLength={120}
              aria-invalid={Boolean(fieldError('serviceName'))}
              aria-describedby={fieldError('serviceName') ? `${ariaBase}-name-err` : undefined}
              onChange={(e) => setField('serviceName', e.target.value)}
              placeholder="e.g. Gateway audit relay"
              autoComplete="off"
            />
            {fieldError('serviceName') && <span className="icp-field__error" id={`${ariaBase}-name-err`}>{fieldError('serviceName').message}</span>}
          </div>

          <div className="icp-field">
            <label className="icp-label" htmlFor={`${ariaBase}-endpoint`}>Endpoint URL</label>
            <input
              id={`${ariaBase}-endpoint`}
              ref={endpointRef}
              className="icp-input"
              type="url"
              inputMode="url"
              value={draft.endpoint}
              aria-invalid={Boolean(fieldError('endpoint'))}
              aria-describedby={fieldError('endpoint') ? `${ariaBase}-endpoint-err` : undefined}
              onChange={(e) => setField('endpoint', e.target.value)}
              placeholder="https://host.internal/api"
              autoComplete="off"
              spellCheck={false}
            />
            {fieldError('endpoint') && <span className="icp-field__error" id={`${ariaBase}-endpoint-err`}>{fieldError('endpoint').message}</span>}
          </div>

          <div className="icp-field">
            <label className="icp-label" htmlFor={`${ariaBase}-cred`}>
              Credential
              <span className="icp-label__hint">never leaves local storage</span>
            </label>
            <select
              id={`${ariaBase}-cred`}
              className="icp-select"
              value={draft.credentialMode}
              onChange={(e) => setField('credentialMode', e.target.value)}
            >
              {CREDENTIAL_MODES.map((mode) => <option key={mode.value} value={mode.value}>{mode.label}</option>)}
            </select>
          </div>

          <div className="icp-field">
            <label className="icp-label" htmlFor={`${ariaBase}-secret`}>Secret reference</label>
            <input
              id={`${ariaBase}-secret`}
              className="icp-input"
              type="password"
              value={draft.secret}
              disabled={draft.credentialMode !== 'bound secret'}
              aria-invalid={Boolean(fieldError('secret'))}
              aria-describedby={fieldError('secret') ? `${ariaBase}-secret-err` : undefined}
              onChange={(e) => setField('secret', e.target.value)}
              placeholder={draft.credentialMode === 'bound secret' ? 'Name of the stored secret' : 'Not used with this mode'}
              autoComplete="new-password"
            />
            {fieldError('secret') && <span className="icp-field__error" id={`${ariaBase}-secret-err`}>{fieldError('secret').message}</span>}
          </div>

          <div className="icp-field icp-field--wide">
            <span className="icp-label">Authorization</span>
            <div className="icp-checks">
              <label className="icp-check">
                <input
                  type="checkbox"
                  checked={draft.authorizedOnly}
                  aria-invalid={Boolean(fieldError('authorizedOnly'))}
                  aria-describedby={fieldError('authorizedOnly') ? `${ariaBase}-auth-err` : undefined}
                  onChange={(e) => setField('authorizedOnly', e.target.checked)}
                />
                This endpoint is an authorized target only
              </label>
            </div>
            {fieldError('authorizedOnly') && <span className="icp-field__error" id={`${ariaBase}-auth-err`}>{fieldError('authorizedOnly').message}</span>}
          </div>
        </div>

        <div className="icp-actions">
          <div className={`icp-result${testStatus === 'fail' ? ' icp-result--fail' : ''}`} role="status" aria-live="polite">
            {testStatus === 'testing' && <span>Testing connectivity…</span>}
            {testStatus === 'ok' && <span><strong>Connection succeeded.</strong>{selectedConfigId ? ' Saved configuration marked authorized.' : ' Save the configuration to store it locally.'}</span>}
            {testStatus === 'fail' && (
              <>
                <span><strong>Connection failed:</strong> {testFailureReason} Values kept — correct them and retest.</span>
                <button type="button" className="icp-result__retry" onClick={testDraft} disabled={testing}>Retry test</button>
              </>
            )}
            {testStatus === 'idle' && <span>Endpoint: {draft.endpoint.trim() || 'not set'}</span>}
          </div>
          <button type="button" className="icp-btn" onClick={testDraft} disabled={testing || saving}>
            {testing ? 'Testing…' : 'Test connectivity'}
          </button>
          <button type="button" className="icp-btn" onClick={cancelEdit} disabled={saving}>
            {selectedConfigId ? 'Cancel edit' : 'Cancel'}
          </button>
          <button type="submit" className="icp-btn icp-btn--primary" disabled={saving || testing}>
            {saving ? 'Saving…' : 'Save configuration'}
          </button>
        </div>
      </form>
    );
  };

  return (
    <section className="icp-root" aria-labelledby={`${ariaBase}-panel-title`}>
      <div className="icp-rail" data-content-rail="workspace">
        <div className="icp-panel">
          <header className="icp-panel__head">
            <div className="icp-panel__heading">
              <span className="icp-panel__kicker">Authorized services</span>
              <h2 className="icp-panel__title" id={`${ariaBase}-panel-title`}>Integration configuration</h2>
            </div>
            <span className="icp-chip">Authorized target only</span>
          </header>

          {savedNotice && (
            <div className="icp-flash" ref={flashRef} tabIndex={-1} role="status" aria-live="polite">
              <div className="icp-flash__body">
                <span>Configuration saved — {savedNotice.serviceName}</span>
                <StatusBadge state="untested" />
                <code className="icp-flash__meta">{fmtStamp(savedNotice.timestamp)}</code>
              </div>
              <button type="button" className="icp-btn icp-btn--small" onClick={() => setSavedNotice(null)}>Dismiss</button>
            </div>
          )}

          {notice && (
            <div className="icp-error" role="alert">
              <span className="icp-error__kicker">{notice.kind === 'error' ? 'Remove failed' : 'Notice'}</span>
              <p className="icp-error__reason">{notice.text}</p>
            </div>
          )}

          {loading && records.length === 0 && (
            <div aria-busy="true" aria-live="polite">
              <p className="icp-section-label">Reading local configurations…</p>
              <ul className="icp-loading" aria-hidden="true">
                <li className="icp-skeleton" />
                <li className="icp-skeleton" />
                <li className="icp-skeleton" />
              </ul>
            </div>
          )}

          {!loading && error && records.length === 0 && renderReadError()}

          {!loading && !(error && records.length === 0) && (
            <>
              {records.length > 0 && renderList()}
              {error && records.length > 0 && (
                <div className="icp-error" role="alert">
                  <span className="icp-error__kicker">Refresh failed</span>
                  <p className="icp-error__reason">{errMsg(error, 'The latest configurations could not be read.')}</p>
                  <button type="button" className="icp-btn icp-btn--small" onClick={retryRead} disabled={readRetrying}>
                    {readRetrying ? 'Retrying…' : 'Retry read'}
                  </button>
                </div>
              )}
              {renderForm()}
            </>
          )}
        </div>
      </div>
    </section>
  );
}